// Generates a print-ready PDF of mailing labels on the Avery 8162 / 5162 grid
// (US Letter, 1-1/3" x 4" labels, 2 columns x 7 rows = 14 per sheet).
//
// Each 4" x 1.33" label is split horizontally:
//   - left 1.5"  : centered QR code encoding the lead's personalized landing link
//   - right 2.5" : the mailing address (contact, dealership, street, city/state/zip)
//
// Layout constants are in inches. They follow Avery's published 5162/8162 spec and
// may need a fraction-of-a-mm tune after a real test print.
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { landingUrlFor } from '../fields';

// Page size is set via jsPDF format: 'letter' (8.5" x 11").
const TOP_MARGIN = 0.83;
const LEFT_MARGIN = 0.19;
const LABEL = { w: 4.0, h: 1.33 };
const H_PITCH = 4.19; // column origin spacing (4.0 label + 0.19 gutter)
const V_PITCH = 1.33; // row origin spacing (no vertical gap)
const COLS = 2;
const ROWS = 7;
const PER_PAGE = COLS * ROWS;

const QR_ZONE_W = 1.5; // left zone
const ADDR_ZONE_X = 1.5; // address zone starts here within the label
const ADDR_ZONE_W = 2.5; // right zone

const ADDR_PAD = 0.1; // inner padding for the address text
const LINE_HEIGHT = 0.16;
const FONT_SIZE = 10; // points
const QR_Y_NUDGE = 0.06; // shift QR down slightly from true vertical center

// A lead is mailable only if it has a street plus some locality.
export const hasAddress = (lead) =>
  !!(lead && lead.addr1 && (lead.city || lead.zip));

// Builds the standard address block lines, skipping empties.
const addressLines = (lead) => {
  const cityStateZip = [
    [lead.city, lead.state].filter(Boolean).join(', '),
    lead.zip,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  return [
    lead.contactName,
    lead.businessName,
    lead.addr1,
    lead.addr2,
    cityStateZip,
  ]
    .map((l) => (l || '').trim())
    .filter(Boolean);
};

export async function generateAvery8162Pdf(leads, filename = 'sales-leads-labels.pdf') {
  const doc = new jsPDF({ unit: 'in', format: 'letter' });

  // Pre-generate each QR once (keyed by leadId) to avoid duplicate work.
  const qrByLead = {};
  await Promise.all(
    leads.map(async (lead) => {
      qrByLead[lead.leadId] = await QRCode.toDataURL(landingUrlFor(lead.queryString), {
        margin: 0,
        width: 256,
        errorCorrectionLevel: 'M',
      });
    })
  );

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SIZE);

  leads.forEach((lead, i) => {
    const slot = i % PER_PAGE;
    if (i > 0 && slot === 0) doc.addPage();

    const col = slot % COLS;
    const row = Math.floor(slot / COLS);
    const x = LEFT_MARGIN + col * H_PITCH;
    const y = TOP_MARGIN + row * V_PITCH;

    // QR zone (left 1.5"): square, centered both ways.
    const qrSize = Math.min(QR_ZONE_W, LABEL.h) - 0.28;
    const qrX = x + (QR_ZONE_W - qrSize) / 2;
    const qrY = y + (LABEL.h - qrSize) / 2 + QR_Y_NUDGE;
    const qr = qrByLead[lead.leadId];
    if (qr) doc.addImage(qr, 'PNG', qrX, qrY, qrSize, qrSize);

    // Address zone (right 2.5"): left-aligned, block vertically centered.
    const textX = x + ADDR_ZONE_X + ADDR_PAD;
    const maxW = ADDR_ZONE_W - ADDR_PAD * 2;
    // Wrap any over-long line (e.g. a long business name) into its own visual
    // lines up front, so the next address line can't overlap the wrapped text.
    const lines = addressLines(lead).flatMap((line) => doc.splitTextToSize(line, maxW));
    // Tighten line spacing if a tall block would overflow the 1.33" label height.
    const lineH = Math.min(LINE_HEIGHT, (LABEL.h - 0.12) / lines.length);
    const blockH = lines.length * lineH;
    let textY = y + (LABEL.h - blockH) / 2 + lineH * 0.75;
    lines.forEach((line) => {
      doc.text(line, textX, textY);
      textY += lineH;
    });
  });

  doc.save(filename);
}
